import { registerLocaleData } from '@angular/common';
import localeDe from '@angular/common/locales/de';
import {
    Component,
    computed,
    HostListener,
    inject,
    NgZone,
    Renderer2,
    signal,
    ChangeDetectionStrategy,
} from '@angular/core';
import { Router } from '@angular/router';
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { ScreenOrientation } from '@capacitor/screen-orientation';
import { SplashScreen } from '@capacitor/splash-screen';
import { StatusBar, Style } from '@capacitor/status-bar';
import { IonApp, IonContent, IonFooter, IonRouterOutlet, Platform } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { setDefaultOptions } from 'date-fns';
import { de } from 'date-fns/locale';
import { register } from 'swiper/element/bundle';
import { HostListenerService } from '../../../../libs/common/services/host-listener/host-listener.service';
import { SplashScreenStateService } from '../../../../libs/common/services/splash-screen-state.service';
import { ThemeService } from '../../../../libs/common/services/theme/theme.service';
import { ToolbarService } from '../../../../libs/common/services/toolbar/toolbar.service';
import { ConfigService } from '../../../../libs/config/services/config.service';
import { Logger, LoggingService, LogLevel } from '../../../../libs/logging/logging.service';
import translationsDE from '../../../../libs/translation/i18n/de.json';
import { SideMenuComponent } from '../../../../libs/common/components/side-menu/side-menu.component';
import { ToolbarComponent } from '../../../../libs/common/components/toolbar/toolbar.component';
import { SplashComponent } from '../../../../libs/common/components/splash/splash.component';
import { FooterConsoleLogComponent } from '../../../../libs/logging/components/footer-console-log/footer-console-log.component';
import { addIcons } from 'ionicons';
import * as ionIcons from 'ionicons/icons';
import { PrivacyScreen } from '@capacitor-community/privacy-screen';
import { EdgeToEdge } from '@capawesome/capacitor-android-edge-to-edge-support';
import { CurafidaAuthService } from '../../../../libs/authentication/curafida-auth.service';
import { UserRoles } from '../../../../libs/user/entities/user-roles';
import { ToolbarPatientComponent } from '../../../../libs/common/components/toolbar-web-patient/toolbar-patient.component';
import { StyleService } from '../../../../libs/common/services/style/style.service';
import { UserPreferencesService } from '../../../../libs/user/services/user-preferences/user-preferences.service';
import { PatientSidePanelComponent } from '../../../../libs/user/components/patient-side-panel/patient-side-panel.component';

register();

@Component({
    selector: 'root',
    templateUrl: 'app.component.html',
    styleUrls: ['app.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        IonApp,
        SideMenuComponent,
        ToolbarComponent,
        IonRouterOutlet,
        SplashComponent,
        FooterConsoleLogComponent,
        IonContent,
        IonFooter,
        ToolbarPatientComponent,
        PatientSidePanelComponent,
    ],
})
export class AppComponent {
    readonly authService = inject(CurafidaAuthService);
    readonly configService = inject(ConfigService);
    readonly userPreferencesService = inject(UserPreferencesService);
    readonly toolbarService = inject(ToolbarService);
    readonly isAppInitialized = signal(false);
    readonly UserRoles = UserRoles;
    private readonly styleService = inject(StyleService);
    private readonly themeService = inject(ThemeService);
    private readonly log: Logger = inject(LoggingService).getLogger(this.constructor.name);
    private readonly platform = inject(Platform);
    private readonly translate = inject(TranslateService);
    protected readonly splashScreenStateService = inject(SplashScreenStateService);
    private readonly hostListenerService = inject(HostListenerService);
    private readonly router = inject(Router);
    private readonly zone = inject(NgZone);
    private readonly renderer = inject(Renderer2);
    readonly backgroundImageUrl = computed<string>(() => {
        const prefs = this.userPreferencesService.userPreferences();
        const url = this.configService.backgroundUrl();
        const isMiniMode = prefs?.currentMode === 'MINI' || !prefs?.currentMode;
        const hasBackgroundUrl = Boolean(url);
        return isMiniMode && hasBackgroundUrl ? `url(${url})` : 'none';
    });

    constructor() {
        addIcons(ionIcons);

        if (
            window.location.hostname + ':' + window.location.port === 'localhost:8100' ||
            window.location.hostname === 'curafida.localhost'
        ) {
            this.log.setLevel(LogLevel.TRACE);
        } else {
            this.log.setLevel(LogLevel.INFO);
        }

        this.setLocalization();
        this.initializeApp();
    }

    @HostListener('document:visibilitychange', ['$event'])
    handleVisibilityChange(event: Event): void {
        this.hostListenerService.visibilityChangeEvent.next(event);
    }

    private async initializeApp(): Promise<void> {
        const start = performance.now();

        this.themeService.init();
        this.styleService.init();

        if (Capacitor.isNativePlatform()) await this.setNativeConfigurations();

        this.log.debug(`Platform is ready for using native features: ${this.platform.platforms()}`);

        await App.addListener('appUrlOpen', (event) => {
            this.zone.run(() => {
                const slug = event.url.split(this.configService.frontendConfig().deepLinking.host).pop();
                if (slug) {
                    this.router.navigateByUrl(slug);
                }
            });
        });
        this.isAppInitialized.set(true);
        const loader = this.renderer.selectRootElement('#loader');
        this.renderer.setStyle(loader, 'display', 'none');

        await this.hideSplash();

        this.log.debug(`[initializeApp] took ${(performance.now() - start).toFixed(0)}ms`);
    }

    private async setNativeConfigurations(): Promise<void> {
        await StatusBar.setStyle({ style: Style.Light });

        if (this.platform.is('android')) {
            await EdgeToEdge.enable();
            await EdgeToEdge.setStatusBarColor({
                color: this.configService.frontendConfig().theme?.colors?.primary,
            });
            await EdgeToEdge.setNavigationBarColor({
                color: this.configService.frontendConfig().theme?.colors?.primary,
            });
        } else if (this.platform.is('ios')) {
            await StatusBar.setBackgroundColor({ color: this.configService.frontendConfig().theme?.colors?.primary });
        }

        await StatusBar.show();

        if (this.configService.frontendConfig().security?.enableScreenshots) {
            await PrivacyScreen.disable();
        } else {
            await PrivacyScreen.enable();
        }

        if (this.platform.is('tablet')) {
            await ScreenOrientation.unlock();
            await ScreenOrientation.lock({ orientation: 'landscape' });
        } else {
            await ScreenOrientation.unlock();
            await ScreenOrientation.lock({ orientation: 'portrait-primary' });
        }
    }

    private async hideSplash(): Promise<void> {
        await SplashScreen.hide();
        this.splashScreenStateService.stop();
    }

    private setLocalization(): void {
        this.translate.setTranslation('de', translationsDE);
        this.translate.addLangs(['de']);
        this.translate.setDefaultLang('de');
        this.translate.use('de');
        registerLocaleData(localeDe, 'de');
        setDefaultOptions({ locale: de });
    }
}
